/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {type APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';

export type AgentActionType = {
  name: string;
  label: string;
};

export const useGetAgentActionTypes = () => {
  const agentsAPI = useAgentsAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionTypes, setActionTypes] = useState<AgentActionType[]>([]);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await agentsAPI({
        eventName: AGENTS_API_EVENT_NAMES.GetActionTypes,
        methodName: METHODS.GET,
      } as APIParams);

      const rawActionTypes = result.actionTypes;
      const mappedActionTypes: AgentActionType[] = rawActionTypes.map((type: any) => {
        // Transform name by replacing underscores with spaces and capitalizing each word
        const label = type.name
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        return {
          name: type.name,
          label,
        };
      });

      setActionTypes(mappedActionTypes);
      setIsLoading(false);
      return mappedActionTypes;
    } catch (err) {
      setIsLoading(false);
      setError('Failed to get agent action types');
      return Promise.reject(err);
    }
  }, [agentsAPI]);

  return {
    actionTypes,
    isLoading,
    error,
    get,
  };
};
